import { use } from 'react';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Variant } from '@nucleus-commerce/api-client/admin';
import { toMajorString } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { StorePricingContext, type StorePricingDrafts } from '../hooks/use-store-pricing-drafts';
import { draftFromRow, inheritedPrice, priceSource } from '../schemas/pricing-rules';

import { PriceSourceBadge } from './price-source-badge';

/** An edited, unsaved store cell, tinted like the master cells. */
const DIRTY = 'border-primary bg-primary/5';

function useCell(variant: Variant) {
  const pricing = use(StorePricingContext);
  const row = pricing?.rowOf(variant.id);
  if (!pricing || !row) return null;
  return { pricing, row, draft: pricing.draftFor(row), saved: draftFromRow(row) };
}

function Pending() {
  return <span className="text-muted-foreground">—</span>;
}

function CellError({
  pricing,
  id,
  field,
}: {
  pricing: StorePricingDrafts;
  id: string;
  field: string;
}) {
  const error = pricing.rowErrors.get(id);
  return error?.field === field ? (
    <p role="alert" className="text-destructive text-xs whitespace-normal">
      {error.message}
    </p>
  ) : null;
}

export function StorePriceCell({
  variant,
  field,
  className = 'w-32',
}: {
  variant: Variant;
  field: 'price' | 'compareAt';
  /** The width; a variant row fills its column. */
  className?: string;
}) {
  const { t } = useTranslation('storeCatalog');
  const cell = useCell(variant);
  if (!cell) return <Pending />;
  const { pricing, draft, saved, row } = cell;
  const dirty = (draft[field]?.amount ?? null) !== (saved[field]?.amount ?? null);
  const inherited = inheritedPrice(row);
  return (
    <div className="flex w-full flex-col gap-1">
      <MoneyInput
        currency={pricing.currency}
        value={draft[field]}
        disabled={!pricing.canWrite || (field === 'compareAt' && !draft.price)}
        placeholder={
          field === 'price'
            ? inherited
              ? toMajorString(inherited)
              : t('pricing.inheritsBase')
            : undefined
        }
        aria-label={t(`pricing.columns.${field}For`, { variant: variant.title })}
        aria-invalid={pricing.rowErrors.get(variant.id)?.field === field}
        onChange={(value) => pricing.setDraft(variant.id, { ...draft, [field]: value })}
        className={cn('h-8 pl-12 text-xs', className, dirty && DIRTY)}
      />
      <CellError pricing={pricing} id={variant.id} field={field} />
    </div>
  );
}

export function StoreSkuCell({
  variant,
  className = 'w-44',
}: {
  variant: Variant;
  className?: string;
}) {
  const { t } = useTranslation('storeCatalog');
  const cell = useCell(variant);
  if (!cell) return <Pending />;
  const { pricing, draft, saved, row } = cell;
  return (
    <div className="flex w-full flex-col gap-1">
      <Input
        value={draft.skuOverride}
        disabled={!pricing.canWrite}
        placeholder={row.sku ?? ''}
        spellCheck={false}
        aria-label={t('pricing.columns.skuFor', { variant: variant.title })}
        aria-invalid={pricing.rowErrors.get(variant.id)?.field === 'skuOverride'}
        onChange={(e) => pricing.setDraft(variant.id, { ...draft, skuOverride: e.target.value })}
        className={cn(
          'h-8 font-mono text-xs',
          className,
          draft.skuOverride.trim() !== saved.skuOverride && DIRTY,
        )}
      />
      <CellError pricing={pricing} id={variant.id} field="skuOverride" />
    </div>
  );
}

export function AvailableCell({ variant }: { variant: Variant }) {
  const { t } = useTranslation('storeCatalog');
  const cell = useCell(variant);
  if (!cell) return <Pending />;
  const { pricing, draft, saved } = cell;
  return (
    <div
      className={cn(
        'inline-flex rounded-full p-0.5',
        draft.isAvailable !== saved.isAvailable && 'ring-primary ring-1',
      )}
    >
      <Switch
        checked={draft.isAvailable}
        disabled={!pricing.canWrite}
        aria-label={t('pricing.columns.availableFor', { variant: variant.title })}
        onCheckedChange={(isAvailable) => pricing.setDraft(variant.id, { ...draft, isAvailable })}
      />
    </div>
  );
}

/** What the store's default market charges, from the saved prices; it follows after Save. */
export function EffectiveCell({ variant }: { variant: Variant }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const cell = useCell(variant);
  if (!cell) return <Pending />;
  const { row, pricing } = cell;
  return (
    <div className="flex flex-col items-start gap-1">
      {row.effective ? (
        <span className="font-medium tabular-nums">
          {format.money(row.effective)}
          {row.effectiveCompareAt ? (
            <span className="text-muted-foreground ml-1.5 text-xs line-through">
              {format.money(row.effectiveCompareAt)}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="text-muted-foreground text-xs">
          {t('pricing.noRate', { currency: pricing.currency })}
        </span>
      )}
      <PriceSourceBadge source={priceSource(row)} />
    </div>
  );
}

/** A group's price range, from the rows' drafts and, where they have none, what they sell at. */
export function StorePriceRange({ variants }: { variants: readonly Variant[] }) {
  const format = useFormat();
  const pricing = use(StorePricingContext);
  const prices = variants.flatMap((variant) => {
    const row = pricing?.rowOf(variant.id);
    if (!row) return [];
    const price = pricing?.draftFor(row).price ?? row.effective;
    return price ? [price] : [];
  });
  const [first] = prices;
  if (!first) return null;
  const amounts = prices.map((price) => price.amount);
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);
  const money = (amount: number) => format.money({ amount, currency: first.currency });
  return <>{low === high ? money(low) : `${money(low)} – ${money(high)}`}</>;
}

export function SellSummary({ variants }: { variants: readonly Variant[] }) {
  const { t } = useTranslation('storeCatalog');
  const pricing = use(StorePricingContext);
  const selling = variants.filter((variant) => {
    const row = pricing?.rowOf(variant.id);
    return row ? pricing?.draftFor(row).isAvailable : false;
  });
  return <>{t('storeVariants.sellCount', { count: selling.length, total: variants.length })}</>;
}
